import React, { useEffect, useMemo, useState } from 'react';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ShieldCheck, AlertTriangle, Clock, ChevronRight, Filter, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { presetRange, rangeEquals } from '@/components/mystery_shopping/FiltersBar';
import { SortHeader } from '@/components/SortHeader';

// sessionStorage key for preserving the queue's filters/search/sort/scroll
// across refresh and same-tab navigation returns.
const QA_QUEUE_STATE_KEY = 'qa_queue_state_v1';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open (Submitted + In Review + Revised)' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_review', label: 'In Review' },
  { value: 'revised', label: 'Revised (resubmitted)' },
  { value: 'revision_requested', label: 'Awaiting satisFINDER' },
  { value: 'awaiting_revision', label: 'Awaiting Revision' },
  { value: 'published', label: 'Published' },
  { value: 'rejected', label: 'Rejected' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '—'; }
}

function tatRemaining(deadlineIso) {
  if (!deadlineIso) return null;
  const deadline = new Date(deadlineIso).getTime();
  if (Number.isNaN(deadline)) return null;
  const now = Date.now();
  const diffMs = deadline - now;
  const hours = Math.round(diffMs / (1000 * 60 * 60));
  return hours;
}

function StatusBadge({ status }) {
  const map = {
    submitted:           { label: 'Submitted',           cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
    in_review:           { label: 'In Review',           cls: 'bg-blue-100 text-blue-800 border border-blue-200' },
    revised:             { label: 'Revised',             cls: 'bg-indigo-100 text-indigo-800 border border-indigo-200' },
    revision_requested:  { label: 'Awaiting satisFINDER', cls: 'bg-purple-100 text-purple-800 border border-purple-200' },
    awaiting_revision:   { label: 'Awaiting Revision',   cls: 'bg-purple-100 text-purple-800 border border-purple-200' },
    published:           { label: 'Published',           cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
    rejected:            { label: 'Rejected',            cls: 'bg-rose-100 text-rose-800 border border-rose-200' },
  };
  const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border border-gray-200' };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`} data-testid={`qa-status-badge-${status}`}>{m.label}</span>;
}

function TatChip({ row }) {
  const hrs = tatRemaining(row.tat_deadline);
  if (row.tat_overdue || (hrs != null && hrs < 0)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200" data-testid="tat-overdue">
        <AlertTriangle className="w-3 h-3" /> Overdue {hrs != null ? `${Math.abs(hrs)}h` : ''}
      </span>
    );
  }
  if (hrs == null) return <span className="text-xs text-[#94A3B8]">—</span>;
  const tone = hrs <= 6 ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tone}`} data-testid="tat-on-time">
      <Clock className="w-3 h-3" /> {hrs}h left
    </span>
  );
}

export default function QAQueue() {
  const { user } = useAuth();
  // Restore prior filters/search/sort/scroll (survives refresh + same-tab back).
  const savedState = React.useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(QA_QUEUE_STATE_KEY) || 'null'); }
    catch { return null; }
  }, []);
  const scrollRestoredRef = React.useRef(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(savedState?.status ?? 'open');
  const [tenantId, setTenantId] = useState(savedState?.tenantId ?? 'all');
  const [search, setSearch] = useState(savedState?.search ?? '');
  const [tenants, setTenants] = useState([]);
  // Date filter — reuses `presetRange` / `rangeEquals` helpers from the
  // Mystery Shopping FiltersBar so the UX is identical. Default is All Time.
  const [range, setRange] = useState(() => savedState?.range || presetRange('all_time'));
  const [customOpen, setCustomOpen] = useState(false);
  const activePreset = useMemo(() => {
    if (rangeEquals(range, presetRange('all_time'))) return 'all_time';
    if (rangeEquals(range, presetRange('today'))) return 'today';
    if (rangeEquals(range, presetRange('this_month'))) return 'this_month';
    if (rangeEquals(range, presetRange('last_month'))) return 'last_month';
    return 'custom';
  }, [range]);
  const presetClick = (kind) => {
    if (kind === 'custom') { setCustomOpen(true); return; }
    setCustomOpen(false);
    setRange(presetRange(kind));
  };
  // Sort — same shape as ReportsList: { key, dir }. Null means "default".
  // Default backend order is `tat_deadline` asc; leaving `sort` null
  // preserves that.
  const [sort, setSort] = useState(savedState?.sort ?? null);
  const onSortClick = (k) => {
    setSort(prev => {
      if (!prev || prev.key !== k) return { key: k, dir: 'desc' };
      if (prev.dir === 'desc') return { key: k, dir: 'asc' };
      return null; // third click → back to default backend order
    });
  };

  const isPrivileged = ['qa', 'platform_admin', 'super_admin'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== 'open') params.set('status', status);
      if (tenantId && tenantId !== 'all') params.set('tenant_id', tenantId);
      if (range.from) params.set('date_from', range.from);
      if (range.to)   params.set('date_to',   range.to);
      // Spec: filter by Visit date only (existing pattern does not support
      // switching date field cleanly).
      if (range.from || range.to) params.set('date_field', 'visit_datetime');
      const res = await fetch(`${API}/qa/queue${params.toString() ? `?${params.toString()}` : ''}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) {
          toast.error('Access denied');
          setRows([]);
          return;
        }
        throw new Error(`Failed: ${res.status}`);
      }
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      // Derive tenant options from queue rows; only refresh when 'all tenants'
      // filter is selected so options don't collapse to a single one when
      // the user narrows the filter.
      if (tenantId === 'all') {
        const uniq = new Map();
        (Array.isArray(data) ? data : []).forEach(r => {
          if (r.tenant_id && !uniq.has(r.tenant_id)) uniq.set(r.tenant_id, r.tenant_name || r.tenant_id);
        });
        setTenants(Array.from(uniq, ([tenant_id, name]) => ({ tenant_id, name })));
      }
    } catch (e) {
      toast.error('Could not load QA queue');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, tenantId, range.from, range.to]);

  // Persist filter/search/sort selections (merged, so scrollY is kept) for
  // refresh + same-tab return.
  useEffect(() => {
    try {
      const prev = JSON.parse(sessionStorage.getItem(QA_QUEUE_STATE_KEY) || '{}') || {};
      sessionStorage.setItem(QA_QUEUE_STATE_KEY, JSON.stringify({ ...prev, status, tenantId, search, range, sort }));
    } catch { /* ignore */ }
  }, [status, tenantId, search, range, sort]);

  // Save scroll position (throttled) so it can be restored on return/refresh.
  useEffect(() => {
    let t = null;
    const onScroll = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        try {
          const prev = JSON.parse(sessionStorage.getItem(QA_QUEUE_STATE_KEY) || '{}') || {};
          sessionStorage.setItem(QA_QUEUE_STATE_KEY, JSON.stringify({ ...prev, scrollY: window.scrollY }));
        } catch { /* ignore */ }
      }, 250);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (t) clearTimeout(t); };
  }, []);

  // Restore scroll position once, after the first load paints the rows.
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    const y = savedState?.scrollY;
    if (y && y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
  }, [loading, savedState]);

  // Refresh results when returning to this tab (e.g. after a report is saved
  // or published in a new tab) — without clearing the current selections.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line
  }, [status, tenantId, range.from, range.to]);

  // Apply search (client-side, existing behaviour) → then optional sort.
  // Sorting must apply to the full filtered result set (the whole queue
  // fits in one request; backend caps at 500 which is our page ceiling).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? rows : rows.filter(r =>
      (r.public_id || '').toLowerCase().includes(q) ||
      (r.tenant_name || '').toLowerCase().includes(q) ||
      (r.branch_name || '').toLowerCase().includes(q) ||
      (r.survey_name || '').toLowerCase().includes(q) ||
      (r.satisfinder_name || '').toLowerCase().includes(q)
    );
    if (!sort) return base;
    const cmp = (a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      // Missing values (null/undefined/empty) always sort to the bottom
      // regardless of direction, so a mostly-empty column doesn't dominate.
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      const at = new Date(av).getTime();
      const bt = new Date(bv).getTime();
      const aOk = !Number.isNaN(at);
      const bOk = !Number.isNaN(bt);
      if (!aOk && !bOk) return 0;
      if (!aOk) return 1;
      if (!bOk) return -1;
      return sort.dir === 'asc' ? at - bt : bt - at;
    };
    return [...base].sort(cmp);
  }, [rows, search, sort]);

  const overdueCount = rows.filter(r => r.tat_overdue).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qa-queue-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#0EA5E9]" />
              <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A]" data-testid="qa-queue-title">QA Queue</h1>
            </div>
            <p className="text-sm text-[#64748B] mt-1">Mystery Shopping FINDs awaiting review.</p>
          </div>
          <div className="flex items-center gap-3">
            {overdueCount > 0 && (
              <Badge className="bg-rose-100 text-rose-800 border border-rose-200" data-testid="qa-overdue-count">
                <AlertTriangle className="w-3 h-3 mr-1" /> {overdueCount} overdue
              </Badge>
            )}
            <Button variant="outline" onClick={load} disabled={loading} data-testid="qa-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
          {/* Date presets — same UX as Mystery Shopping FiltersBar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex flex-wrap max-w-full rounded-lg bg-[#F1F5F9] p-0.5" data-testid="qa-date-presets">
              {[
                { key: 'all_time',   label: 'All Time' },
                { key: 'today',      label: 'Today' },
                { key: 'this_month', label: 'This Month' },
                { key: 'last_month', label: 'Last Month' },
                { key: 'custom',     label: 'Custom range' },
              ].map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => presetClick(p.key)}
                  data-testid={`qa-preset-${p.key}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    activePreset === p.key
                      ? 'bg-white text-[#0F172A] shadow-sm'
                      : 'text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >{p.label}</button>
              ))}
            </div>
            <span className="text-[11px] text-[#94A3B8]">Filters Visit date</span>
          </div>
          {(customOpen || activePreset === 'custom') && (
            <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-[#F1F5F9]" data-testid="qa-custom-range">
              <div className="space-y-1">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">From</label>
                <Input
                  type="date"
                  value={range.from}
                  onChange={(e) => setRange({ from: e.target.value, to: range.to })}
                  className="h-9 w-[160px]"
                  data-testid="qa-filter-date-from"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">To</label>
                <Input
                  type="date"
                  value={range.to}
                  onChange={(e) => setRange({ from: range.from, to: e.target.value })}
                  className="h-9 w-[160px]"
                  data-testid="qa-filter-date-to"
                />
              </div>
            </div>
          )}
          {/* Existing status / tenant / search filters — unchanged */}
          <div className="flex flex-wrap gap-3 items-center pt-1 border-t border-[#F1F5F9]">
            <div className="flex items-center gap-2 text-sm text-[#475569]">
              <Filter className="w-4 h-4" /> Filters
            </div>
            <div className="min-w-[220px]">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="qa-filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isPrivileged && (
              <div className="min-w-[220px]">
                <Select value={tenantId} onValueChange={setTenantId}>
                  <SelectTrigger data-testid="qa-filter-tenant">
                    <SelectValue placeholder="Tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tenants</SelectItem>
                    {tenants.map(t => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name || t.tenant_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#94A3B8]" />
              <Input
                placeholder="Search FIND ID / tenant / branch / satisFINDER"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="qa-search-input"
              />
            </div>
          </div>
        </div>

        {/* Filtered report count — reflects status, tenant, date range and
            search across the whole result set (no server pagination). */}
        <div className="flex items-center justify-between px-0.5">
          <div className="text-sm font-semibold text-[#475569]" data-testid="qa-report-count">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead>FIND ID</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Survey</TableHead>
                <TableHead>satisFINDER</TableHead>
                <SortHeader label="Visit"      sortKey="visit_datetime"      sortState={sort} onSortClick={onSortClick} testIdPrefix="qa-sort" />
                <SortHeader label="Submitted"  sortKey="submission_datetime" sortState={sort} onSortClick={onSortClick} testIdPrefix="qa-sort" />
                <SortHeader label="TAT"        sortKey="tat_deadline"        sortState={sort} onSortClick={onSortClick} testIdPrefix="qa-sort" />
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-[#64748B]">Loading…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-[#64748B]" data-testid="qa-queue-empty">No FINDs match the current filters.</TableCell></TableRow>
              ) : filtered.map(r => (
                <TableRow key={r.response_id} className="hover:bg-[#F8FAFC]" data-testid={`qa-row-${r.response_id}`}>
                  <TableCell className="font-mono text-xs">
                    <a
                      href={`/qa-review/${r.response_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1A3E6F] hover:underline"
                      data-testid={`qa-find-link-${r.response_id}`}
                    >{r.public_id || r.response_id}</a>
                  </TableCell>
                  <TableCell>{r.tenant_name || '—'}</TableCell>
                  <TableCell>{r.branch_name || '—'}</TableCell>
                  <TableCell>{r.survey_name || '—'}</TableCell>
                  <TableCell>{r.satisfinder_name || '—'}</TableCell>
                  <TableCell className="text-sm text-[#475569]">{fmtDate(r.visit_datetime)}</TableCell>
                  <TableCell className="text-sm text-[#475569]">{fmtDate(r.submission_datetime)}</TableCell>
                  <TableCell><TatChip row={r} /></TableCell>
                  <TableCell><StatusBadge status={r.qa_status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" asChild>
                      <a
                        href={`/qa-review/${r.response_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`qa-review-btn-${r.response_id}`}
                      >
                        Review <ChevronRight className="w-4 h-4 ml-1" />
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
}
